import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { Toast } from 'components'; 
import { TOAST_TYPES_ENUM } from 'enums'; 
import { queryClient } from 'App'; 
import { usePWA } from 'hooks/usePWA';
import { mutateWithOffline } from 'utils/mutateWithOffline';
import { removeOfflineMeasurement } from 'hooks/useOfflineMeasurements';
import { getOfflineQueue, removeOfflineOperation } from 'hooks/useOfflineQueue';

type DeleteMeasurementType = {
  measurementUuid: string;
};

const useDeleteMeasurement = (
  orderUuid: string | null, 
  options?: UseMutationOptions<any, Error, DeleteMeasurementType>
) => {
  const { isOffline } = usePWA();

  const refetchMeasurements = async () => {
    if (!orderUuid) return;
    // Odpala queryFn od razu dla aktywnych obserwatorów (czyli aktualnie oglądanego ekranu)
    await queryClient.refetchQueries({
      queryKey: ['measurements', orderUuid],
      type: 'active',
    });
  };

  const removeFromMeasurementsCache = (measurementUuid: string) => {
    if (!orderUuid) return;

    // `useGetMeasurements` zwraca z query obiekt { measurements: [...] } i dopiero w `select` mapuje to na tablicę.
    // Dlatego musimy aktualizować cache w takim właśnie kształcie.
    queryClient.setQueryData(['measurements', orderUuid], (prev: any) => {
      const prevList = Array.isArray((prev as any)?.measurements)
        ? (prev as any).measurements
        : Array.isArray(prev)
        ? prev
        : [];

      const nextList = prevList.filter((m: any) => m.uuid !== measurementUuid);
      return {
        ...(prev && typeof prev === 'object' ? prev : {}),
        measurements: nextList,
      };
    });
  };

  return useMutation({
    ...options,
    mutationKey: ['deleteMeasurement'],
    mutationFn: async ({ measurementUuid }) => {
      const url = `/measurement?measurementUuid=${encodeURIComponent(measurementUuid)}`;

      // Jeśli user usuwa pomiar utworzony offline, który nie został jeszcze wysłany (pending POST),
      // to NIE mozemy wysyłać DELETE do backendu (bo ten rekord tam nie istnieje).
      // Czyścimy lokalnie kolejkę (POST + ewentualne operacje zależne) oraz offlineMeasurements.
      const queue = getOfflineQueue();
      const pendingPost = queue.find(
        (op) =>
          op.entityType === 'measurement' &&
          op.method === 'POST' &&
          op.tempId === measurementUuid &&
          (op.status === 'pending' || op.status === 'failed')
      );

      if (pendingPost) {
        // usuń sam POST
        removeOfflineOperation(pendingPost.id);

        // usuń operacje powiązane z tym measurementUuid (client-info/install-info/info/files)
        queue
          .filter(
            (op) =>
              op.id !== pendingPost.id &&
              (op.status === 'pending' || op.status === 'failed') &&
              ((typeof op.body === 'object' && op.body && (op.body as any).measurementUuid === measurementUuid) ||
                op.url.includes(`measurementUuid=${encodeURIComponent(measurementUuid)}`) ||
                op.url.includes(`measurementUuid=${measurementUuid}`))
          )
          .forEach((op) => removeOfflineOperation(op.id));

        if (orderUuid) {
          removeOfflineMeasurement(orderUuid, measurementUuid);
        }

        // Natychmiast usuń z listy w UI (React Query cache)
        removeFromMeasurementsCache(measurementUuid);

        queryClient.invalidateQueries({
          queryKey: ['measurements', orderUuid],
        });
        queryClient.invalidateQueries({ queryKey: ['measurements'] });

        // twardy refetch – UI ma się odświeżyć bez przeładowania strony
        await refetchMeasurements();

        return {
          status: 'success',
          message: 'Measurement deleted (offline-local-only)',
        };
      }

      const response = await mutateWithOffline<any>(
        'DELETE',
        url,
        undefined,
        {
          entityType: 'measurement',
          forceOffline: isOffline,
          simulateResponse: () => {

            if (orderUuid) {
              removeOfflineMeasurement(orderUuid, measurementUuid);
              removeFromMeasurementsCache(measurementUuid);

              
            }

            return {
              status: 'success',
              message: 'Measurement deleted (offline)',
            };
          },
        }
      );

      return response;
    },
    onMutate: (variables) => {


      // Optymistycznie zdejmujemy element z listy, żeby zniknął od razu,
      // nawet jeśli w offline kończymy tylko na lokalnych danych.
      removeFromMeasurementsCache(variables.measurementUuid);

      Toast({
        message: 'Przetwarzanie żądania usunięcia pomiaru...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'delete-measurement',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      
      Toast({
        message: 'Pomiar został pomyślnie usunięty.',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'delete-measurement',
      });
      if (orderUuid) {
        // Utrzymujemy spójność kształtu cache ({measurements: []})
        removeFromMeasurementsCache(variables.measurementUuid);

        queryClient.invalidateQueries({
          queryKey: ['measurements', orderUuid],
        });
      }
      queryClient.invalidateQueries({ queryKey: ['measurements'] });

      // twardy refetch w każdej ścieżce sukcesu (online/offline)
      refetchMeasurements();
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      
      Toast({
        message: 'Błąd podczas usuwania pomiaru.',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'delete-measurement',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useDeleteMeasurement;